import React from 'react';
import { useTranslation } from 'react-i18next';
import type { LocaleCode, ShiftRegisterPage } from '@/api/models';
import type { ErrorResponse } from '@/api/handlers/responses';
import {
    ALL_BRANCHES_WITH_ARCHIVED_QUERY,
    useBranchDetailsQuery,
    useShiftRegisterQuery,
} from '@/api/queries';
import {
    resolveLocale,
    unwrapStatement,
} from '@/pages/accounting/statements/statementMeta';
import { getAuthMethod, getUser } from '@/utils/authSession';
import {
    canReadShifts,
    deriveShiftsAccess,
    type ShiftsAccess,
} from './shiftsAccess';
import {
    initialShiftFilters,
    toShiftRegisterFilters,
    toShiftReconciliation,
    toShiftRowModels,
    staleShiftCount,
    type BranchFilterOption,
    type ShiftFilterState,
    type ShiftReconciliation,
    type ShiftRowModel,
    type ShiftStatusFilter,
} from './shiftsMeta';

export type UseShiftsPageResult = {
    access: ShiftsAccess;
    canRead: boolean;
    locale: LocaleCode;
    isLoading: boolean;
    error: ErrorResponse | null;
    onRetry: () => void;
    filters: ShiftFilterState;
    onRangeChange: (from: string, to: string) => void;
    onBranchChange: (branchId: string) => void;
    onStatusChange: (status: string) => void;
    onPageChange: (page: number) => void;
    /** Branches to offer in the filter, id + name. */
    branches: BranchFilterOption[];
    rows: ShiftRowModel[];
    /** Whether every closed drawer on this page reached the ledger (BOS-164). */
    reconciliation: ShiftReconciliation;
    /** Rows on this page open longer than a day (BOS-167). */
    staleCount: number;
    pageInfo: Pick<
        ShiftRegisterPage,
        'page' | 'pageSize' | 'totalCount' | 'totalPages'
    >;
    /** The shift whose rollup drawer is open, or `null`. */
    selectedShiftId: string | null;
    onSelect: (shiftId: string) => void;
    onCloseRollup: () => void;
};

const EMPTY_PAGE = { page: 1, pageSize: 0, totalCount: 0, totalPages: 0 };

/**
 * Drives the shift register.
 *
 * Two reads: the register itself, and the branch list behind its filter. **No
 * write** — this screen summarizes what the till posted and never touches a
 * shift. Opening and closing are the POS's, behind a PIN-only policy this app
 * cannot satisfy.
 *
 * The rollup is not read here. It belongs to the drawer, which owns its own
 * query and its own failure: an unreachable rollup must not replace a working
 * register with a full-page error over one shift that would not open.
 */
export const useShiftsPage = (): UseShiftsPageResult => {
    const { i18n } = useTranslation('pages');
    const locale = resolveLocale(i18n.language);

    const access = React.useMemo(
        () => deriveShiftsAccess(getUser(), getAuthMethod()),
        [],
    );
    const canRead = canReadShifts(access);

    const [filters, setFilters] = React.useState<ShiftFilterState>(() =>
        initialShiftFilters(new Date()),
    );
    const [selectedShiftId, setSelectedShiftId] = React.useState<string | null>(
        null,
    );

    const requestFilters = React.useMemo(
        () => toShiftRegisterFilters(filters),
        [filters],
    );

    const registerQuery = useShiftRegisterQuery(requestFilters, canRead);
    const { data: register, error } = unwrapStatement(
        registerQuery.data,
        registerQuery.error,
    );

    // The branch filter offers every branch, including ones no shift has been
    // opened at yet — the register's own rows could only ever name the branches
    // that already appear in it, which is the wrong list for a filter.
    //
    // BOS-167: **archived branches included.** Archiving pulls a branch out of
    // the pickers you *choose* with, which is right — you cannot sell there any
    // more. It must not pull it out of a filter over history, which is what it
    // did: an archived Batumi's two shifts sat in the register with no way to
    // isolate them. Archived options carry a chip, the same one Team uses.
    const branchesQuery = useBranchDetailsQuery(
        ALL_BRANCHES_WITH_ARCHIVED_QUERY,
        canRead,
    );
    const branchesData = branchesQuery.data;
    const branches = React.useMemo(
        () =>
            branchesData?.success
                ? branchesData.response.items.map((branch) => ({
                      id: branch.id,
                      name: branch.name,
                      archived: branch.archived,
                  }))
                : [],
        [branchesData],
    );

    /**
     * Any filter change resets to page 1. Narrowing a five-page register while
     * sitting on page 4 lands on an empty page that reads as "nothing matched".
     */
    const setFilter = React.useCallback((patch: Partial<ShiftFilterState>) => {
        setFilters((current) => ({ ...current, ...patch, page: 1 }));
    }, []);

    const onRangeChange = React.useCallback(
        (from: string, to: string) => setFilter({ from, to }),
        [setFilter],
    );

    const onBranchChange = React.useCallback(
        (branchId: string) => setFilter({ branchId }),
        [setFilter],
    );

    const onStatusChange = React.useCallback(
        (status: string) => setFilter({ status: status as ShiftStatusFilter }),
        [setFilter],
    );

    const onPageChange = React.useCallback((page: number) => {
        setFilters((current) => ({ ...current, page }));
    }, []);

    const onRetry = React.useCallback(() => {
        registerQuery.refetch();
    }, [registerQuery]);

    return {
        access,
        canRead,
        locale,
        // `keepPreviousData` holds the previous page on screen while the next
        // loads, so this is only true on the very first read — exactly when the
        // table should skeleton its rows.
        isLoading: canRead && registerQuery.isLoading,
        error,
        onRetry,
        filters,
        onRangeChange,
        onBranchChange,
        onStatusChange,
        onPageChange,
        branches,
        rows: React.useMemo(
            () => toShiftRowModels(register?.rows ?? []),
            [register],
        ),
        // BOS-167: how many drawers on this page nobody closed. Surfaced in
        // the subtitle, because a shift open for eleven days had nothing on
        // any screen pointing at it.
        staleCount: staleShiftCount(toShiftRowModels(register?.rows ?? [])),
        // BOS-164: whether every closed drawer on this page reached the books.
        reconciliation: React.useMemo(
            () => toShiftReconciliation(register?.rows ?? []),
            [register],
        ),
        pageInfo: register
            ? {
                  page: register.page,
                  pageSize: register.pageSize,
                  totalCount: register.totalCount,
                  totalPages: register.totalPages,
              }
            : EMPTY_PAGE,
        selectedShiftId,
        onSelect: setSelectedShiftId,
        onCloseRollup: React.useCallback(() => setSelectedShiftId(null), []),
    };
};
